import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Brand, BrandListResponse } from './brands.types';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { getBrandsList, getBrandDetails, createBrand, updateBrand, deleteBrandDetails } from './brands.action';
import snackbarUtils from '../../utils/SnackbarUtils';
import { SET_ROUTE_PARAMS } from '../../redux/constants';
import { ToolkitActionType } from '../../types/utils/toolkit-action.type';
import { RouteParamsType } from '../../types/utils/router-params.type';

interface BrandsState {
  brandsList: BrandListResponse | null;
  brandsListFetchStatus: FETCH_STATUS;
  brandsRouteParams: RouteParamsType;
  brandDetails: Brand | null;
  brandDetailsFetchStatus: FETCH_STATUS;
  brandDetailsCreateStatus: FETCH_STATUS;
  brandDetailsDeleteStatus: FETCH_STATUS;
  brandDetailsUpdateStatus: FETCH_STATUS;
}

export const initialBrandsRouteParams: RouteParamsType = {
  page: 1,
  limit: 10,
  order: '-id',
  q: '',
};

const initialState: BrandsState = {
  brandsList: null,
  brandsListFetchStatus: FETCH_STATUS.NULL,
  brandsRouteParams: initialBrandsRouteParams,
  brandDetails: null,
  brandDetailsFetchStatus: FETCH_STATUS.NULL,
  brandDetailsCreateStatus: FETCH_STATUS.NULL,
  brandDetailsDeleteStatus: FETCH_STATUS.NULL,
  brandDetailsUpdateStatus: FETCH_STATUS.NULL,
};

const brandsSlice = createSlice({
  name: 'brands',
  initialState,
  reducers: {
    setBrandsList: (state, action: PayloadAction<BrandListResponse>) => {
      state.brandsList = action.payload;
    },
    setBrandsListFetchStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.brandsListFetchStatus = action.payload;
    },
    setBrandsRouteParams: (state, action: PayloadAction<Partial<RouteParamsType>>) => {
      state.brandsRouteParams = { ...state.brandsRouteParams, ...action.payload };
    },
    setBrandDetails: (state, action: PayloadAction<Brand | null>) => {
      state.brandDetails = action.payload;
    },
    setBrandDetailsFetchStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.brandDetailsFetchStatus = action.payload;
    },
    setBrandDetailsCreateStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.brandDetailsCreateStatus = action.payload;
    },
    setBrandDetailsUpdateStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.brandDetailsUpdateStatus = action.payload;
    },
    setBrandDetailsDeleteStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.brandDetailsDeleteStatus = action.payload;
    },
    resetBrandsState: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(SET_ROUTE_PARAMS, (state, action) => {
        const { payload } = action as ToolkitActionType<{ reducer: string; data: RouteParamsType }>;
        if (payload.reducer === 'brands') {
          state.brandsRouteParams = { ...state.brandsRouteParams, ...payload.data };
        }
      })

      // Get Brands List
      .addCase(getBrandsList.pending, state => {
        state.brandsListFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getBrandsList.fulfilled, (state, action) => {
        state.brandsList = action.payload;
        state.brandsListFetchStatus = FETCH_STATUS.FULFILLED;
      })
      .addCase(getBrandsList.rejected, state => {
        state.brandsListFetchStatus = FETCH_STATUS.REJECTED;
      })
      // Get Brand Details
      .addCase(getBrandDetails.pending, state => {
        state.brandDetailsFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getBrandDetails.fulfilled, (state, action) => {
        state.brandDetails = action.payload;
        state.brandDetailsFetchStatus = FETCH_STATUS.FULFILLED;
      })
      .addCase(getBrandDetails.rejected, (state, action) => {
        state.brandDetailsFetchStatus = FETCH_STATUS.REJECTED;
        snackbarUtils.error(action.payload);
      })
      // Create Brand
      .addCase(createBrand.fulfilled, (state, action) => {
        state.brandDetailsCreateStatus = FETCH_STATUS.FULFILLED;
        snackbarUtils.success(`Brand ${action.payload?.name} created successfully`);
      })
      .addCase(createBrand.rejected, state => {
        state.brandDetailsCreateStatus = FETCH_STATUS.REJECTED;
        snackbarUtils.error('Failed to create brand');
      })
      // Update Brand
      .addCase(updateBrand.fulfilled, (state, action) => {
        if (state.brandDetails?.id === action.payload.id) {
          state.brandDetails = action.payload;
        }
        state.brandDetailsUpdateStatus = FETCH_STATUS.FULFILLED;
        snackbarUtils.success(`Brand ${action.payload?.name} updated successfully`);
      })
      .addCase(updateBrand.rejected, state => {
        state.brandDetailsUpdateStatus = FETCH_STATUS.REJECTED;
        snackbarUtils.error('Failed to update brand');
      })
      // Delete Brand
      .addCase(deleteBrandDetails.pending, state => {
        state.brandDetailsDeleteStatus = FETCH_STATUS.PENDING;
      })
      .addCase(deleteBrandDetails.fulfilled, (state, action) => {
        state.brandDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
        snackbarUtils.success(`Brand ${action.payload?.name} deleted successfully`);
      })
      .addCase(deleteBrandDetails.rejected, state => {
        state.brandDetailsDeleteStatus = FETCH_STATUS.REJECTED;
        snackbarUtils.error('Failed to delete brand');
      });
  },
});

export const {
  setBrandsList,
  setBrandsListFetchStatus,
  setBrandsRouteParams,
  setBrandDetails,
  setBrandDetailsFetchStatus,
  setBrandDetailsDeleteStatus,
  setBrandDetailsCreateStatus,
  setBrandDetailsUpdateStatus,
  resetBrandsState,
} = brandsSlice.actions;

export default brandsSlice.reducer;
